/**
 * Languages the client app ships.
 *
 * Single source of truth for the switchers in Header, MenuSettings/Settings and
 * the login page, which each used to carry their own hardcoded en/fr pair.
 */

export type LanguageOption = {
  /** i18next code, and the value stored under localStorage.appLanguage. */
  value: string;
  /** i18next key (in the "menu"/"header"/"settings" namespaces) for the label. */
  labelKey: string;
  /** Endonym, for switchers that render a plain string rather than translating. */
  nativeLabel: string;
};

export const LANGUAGES: LanguageOption[] = [
  { value: "en", labelKey: "english", nativeLabel: "English" },
  { value: "fr", labelKey: "french", nativeLabel: "Français" },
  { value: "ar", labelKey: "arabic", nativeLabel: "العربية" },
];

/** Base subtags written right-to-left. */
export const RTL_LANGUAGES: string[] = ["ar"];

export const DEFAULT_LANGUAGE = "en";

export const isSupportedLanguage = (language?: string | null): boolean =>
  !!language && LANGUAGES.some((option) => option.value === language);

/** i18next may report a regional tag ("ar-DZ"); compare on the base subtag. */
const baseSubtag = (language?: string | null): string =>
  (language || DEFAULT_LANGUAGE).split("-")[0];

export const isRtlLanguage = (language?: string | null): boolean =>
  RTL_LANGUAGES.includes(baseSubtag(language));

/**
 * Mirror the document for right-to-left languages.
 *
 * Ionic reads `dir` off <html> to flip its own components (slides, back
 * buttons, item layouts), so this is what makes Arabic lay out correctly rather
 * than merely display Arabic text in a left-to-right frame.
 */
export const applyDirection = (language?: string | null): void => {
  if (typeof document === "undefined") return;
  const base = baseSubtag(language);
  const dir = isRtlLanguage(base) ? "rtl" : "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", base);
  if (document.body) {
    document.body.setAttribute("dir", dir);
  }
};
